import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface ChartCardProps {
  children: ReactNode;
  title: string;
  description?: string;
  /** Optional eyebrow label (uppercase, letterspaced) — sits above the title. */
  eyebrow?: string;
  /** Optional content rendered to the right of the title (e.g. toggle controls). */
  actions?: ReactNode;
  /** Optional source/footnote shown at the bottom in muted body-sm. */
  footer?: ReactNode;
}

const ChartCard: FC<ChartCardProps> = ({ title, children, description, eyebrow, actions, footer }) => {
  return (
    <Box
      sx={{
        my: 2,
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_COIN,
        boxShadow: SHADOW,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0px 16px 40px rgba(21, 24, 58, 0.10)',
        },
      }}
    >
      <Box sx={{ px: { xs: 2.5, md: 3.5 }, pt: 3, pb: footer ? 1.5 : 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2.5,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {eyebrow && (
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: `${NAVY}73`,
                  mb: 0.75,
                }}
              >
                {eyebrow}
              </Typography>
            )}
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.25,
                color: NAVY,
              }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: `${NAVY}80`,
                  fontSize: '0.875rem',
                  mt: 0.5,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
          {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
        </Box>
        {children}
        {footer && (
          <Typography
            sx={{
              mt: 2,
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.75rem',
              color: `${NAVY}66`,
            }}
          >
            {footer}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChartCard;
